import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { setExceptionHandler, getLoaderBtn } from '@app/store/actions';
import { ModalActionsComponent, PasslyTextField } from '@app/components';
import { FormControl } from '@material-ui/core';
import { launchPadService } from '@app/services';
import { onPressEnterModal, genericErrorString } from '@app/helpers';
import { ISingleOwnedWorkflow } from '@app/models';
import { IWorkflowStep } from '@app/pages/ssoManager/addEditApplication.enums';

export interface IReceivedData {
    appId: string;
    dialogOpen: Function;
}

interface workflowStepValues {
    key: string;
    value: string;
}

export default function EditApplicationDialog(props: IReceivedData) {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    const [formValid, setFormValid] = useState(true);
    const [workflowSteps, setWorkflowSteps] = useState<IWorkflowStep[]>([]);
    const [newWorkflowValues, setNewWorkflowValues] = useState<[workflowStepValues]>([{} as workflowStepValues]);

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        launchPadService.getOwnedWorkflow(props.appId, handleSuccessWorkflow, handleError);
    }, []);

    const handleSuccessWorkflow = (response: any) => {
        const result: ISingleOwnedWorkflow = response.data;

        if (result) {
            setWorkflowSteps(JSON.parse(result.Workflow));
        }
    };

    const onFormChange = (e: any) => {
        const { name, value } = e.target;

        if (name && value) {
            let index = -1;
            for (let i: number = 0; i < newWorkflowValues.length; i++) {
                if (newWorkflowValues[i].key === name) {
                    index = i;
                    break;
                }
            }

            if (index > -1) {
                newWorkflowValues.splice(index, 1);
            }

            let newValue: workflowStepValues = {} as workflowStepValues;
            newValue.key = name;
            newValue.value = value;

            newWorkflowValues.push(newValue);

            setNewWorkflowValues(newWorkflowValues);
        }
    };

    const handleError = (error: any) => {
        setErrorMessage(genericErrorString(error));

        dispatch(getLoaderBtn(false));
    };

    const handleCancel = () => {
        props.dialogOpen(false);
    };

    const handleSuccess = () => {
        dispatch(getLoaderBtn(false));
        props.dialogOpen(false);
    };

    const handleSaveBtn = () => {
        dispatch(getLoaderBtn(true));

        let newVals: string = '{';

        for (let i: number = 1; i < newWorkflowValues.length; i++) {
            if (i > 1) {
                newVals += ',';
            }
            newVals += `"${newWorkflowValues[i].key}":"${newWorkflowValues[i].value}"`;
        }

        newVals += '}';

        let jsonObject: any = {};
        jsonObject.WorkflowValues = newVals;

        launchPadService.updateAppAttribute(props.appId, jsonObject, handleSuccess, handleError);
    };

    const isInputStep = (step: IWorkflowStep) => {
        if (step.step && step.step === 'input') {
            return true;
        }
        return false;
    };

    // the reason why the names look backwards in the following methods is because the original coders messed up the naming.
    // will tackle in the redesign
    const workflowAttrLabelValue = (rawVal: string | null) => {
        if (rawVal && rawVal === 'Password') {
            return translation('WORKFLOW_PLACEHOLDER_PASSWORD');
        }

        return translation('WORKFLOW_PLACEHOLDER_USERNAME');
    };

    const workflowAttrPlaceholderValue = (rawVal: string | null) => {
        if (rawVal && rawVal === 'Password') {
            return translation('WORKFLOW_LABEL_PASSWORD');
        }

        return translation('WORKFLOW_LABEL_USERNAME');
    };

    return (
        <>
        <form className='modal__form' method='post' tabIndex={0} onKeyPress={onPressEnterModal(handleSaveBtn, !formValid)}>
            <div className='modal__medium flex1'>
                {workflowSteps.length > 0 &&
                    <div className='modal__body--row'>
                        <h4>{translation('WORKFLOW_ATTRIBUTES')}</h4>

                        {workflowSteps.map((step: IWorkflowStep, index: number) =>
                            <>
                                {isInputStep(step) &&
                                    <div key={index} className='modal__body--inner_row'>
                                        <FormControl>
                                            <PasslyTextField
                                                name={step.elementval}
                                                label={workflowAttrLabelValue(step.value)}
                                                placeholder={workflowAttrPlaceholderValue(step.value)}
                                                onChange={(e) => onFormChange(e)}
                                                id={step.elementval}
                                                type={step.type}
                                            />
                                        </FormControl>
                                    </div>

                                }
                            </>
                        )
                        }
                    </div>
                }
            </div>
            {
                errorMessage &&
                <div className='modal__error'>{errorMessage}</div>
            }
        </form>
            <ModalActionsComponent
                primaryBtn={translation('SAVE_CHANGES')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSaveBtn}
                onClickSecondary={handleCancel}
                disabled={!formValid}
                id='editApp'
            />
        </>
    );
}